import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'
import { useApolloClient } from '@apollo/client'
import { useToast } from '@winnin/winnin_ui.components.toast'

import useSearchParams from 'hooks/useSearchParams'
import { useTranslate } from 'lib/tolgee'
import { NICHE_LIMIT } from 'modules/niche/common/niche.consts'
import { translations as nicheTranslations } from 'modules/niche/common/niche.i18n'
import { getProviders } from 'modules/niche/common/niche.utils'
import type { INicheCard } from 'modules/niche/common/types'
import analyticsTracker from 'utils/analyticsTracker'
import humanizeLargeNumber from 'utils/humanizeLargeNumber'
import TOLGEE_GLOBALS from 'utils/tolgeeGlobals'

import type { ITaxonomyChildrenTab, ITaxonomyChildrenTabProps } from './taxonomy-children-tab.types'
import {
  getTaxonomyChildrenTotalsVariables,
  loadTaxonomyChildrenProgressively,
  sortTaxonomyChildrenByEngagement,
  taxonomyChildrenLoadReducer,
  toggleChartSelection,
} from './taxonomy-children-tab.utils'

export const useTaxonomyChildrenTabModel = ({
  territoryId,
  territoryName,
  feature,
}: ITaxonomyChildrenTabProps): ITaxonomyChildrenTab => {
  const client = useApolloClient()
  const searchParams = useSearchParams()
  const { createToast } = useToast()
  const t = useTranslate()

  const [retryCount, setRetryCount] = useState(0)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [loadState, dispatch] = useReducer(taxonomyChildrenLoadReducer, undefined)

  const uploadDate = searchParams.get('uploadDate')
  const providersKey = getProviders(searchParams).join(',')
  const totalsVariables = useMemo(
    () => getTaxonomyChildrenTotalsVariables(uploadDate, providersKey),
    [uploadDate, providersKey]
  )

  const loadKey = `${territoryId}:${uploadDate}:${providersKey}:${retryCount}`

  useEffect(
    function loadTaxonomyChildren() {
      let isCancelled = false

      loadTaxonomyChildrenProgressively({
        client,
        territoryId,
        variables: totalsVariables,
        onChildrenPage: children => dispatch({ type: 'page-loaded', key: loadKey, children }),
        onChildSettled: (childId, totals) => dispatch({ type: 'child-settled', key: loadKey, childId, totals }),
        isCancelled: () => isCancelled,
      })
        .then(() => {
          if (!isCancelled) dispatch({ type: 'completed', key: loadKey })
        })
        .catch(() => {
          if (!isCancelled) dispatch({ type: 'failed', key: loadKey })
        })

      return () => {
        isCancelled = true
      }
    },
    [client, territoryId, totalsVariables, loadKey]
  )

  const currentLoad = loadState?.key === loadKey ? loadState : undefined
  const children = useMemo(() => currentLoad?.children ?? [], [currentLoad])
  const totalsById = useMemo(() => currentLoad?.totalsById ?? {}, [currentLoad])
  const failedIds = useMemo(() => currentLoad?.failedIds ?? [], [currentLoad])
  const isComplete = Boolean(currentLoad?.isComplete)

  const isChildrenLoading = !children.length && !isComplete
  const settledCount = Object.keys(totalsById).length + failedIds.length
  const isTotalsLoading = !isComplete || settledCount < children.length

  const availableChildren = useMemo(
    () => children.filter(child => !failedIds.includes(child.territoryId)),
    [children, failedIds]
  )

  const haveAllChildrenFailed = isComplete && children.length > 0 && failedIds.length === children.length
  const hasError = Boolean(currentLoad?.hasError) || haveAllChildrenFailed
  const isEmpty = isComplete && !hasError && !children.length

  const sortedChildren = useMemo(
    () => sortTaxonomyChildrenByEngagement(availableChildren, totalsById),
    [availableChildren, totalsById]
  )
  const defaultIds = useMemo(
    () => sortedChildren.slice(0, NICHE_LIMIT).map(child => child.territoryId),
    [sortedChildren]
  )
  const availableSelectedIds = useMemo(
    () => selectedIds.filter(id => availableChildren.some(child => child.territoryId === id)),
    [availableChildren, selectedIds]
  )
  const visibleIds = availableSelectedIds.length ? availableSelectedIds : defaultIds

  const handleToggleChild = useCallback(
    (id: string) => {
      const { TOGGLE_NICHE_VIEW } = analyticsTracker.eventNames
      analyticsTracker.eventTrack(TOGGLE_NICHE_VIEW, { nicheId: id, territoryTitle: territoryName, territoryId })

      const toggle = toggleChartSelection({ id, selectedIds: availableSelectedIds, defaultIds, limit: NICHE_LIMIT })
      if (toggle.status === 'limit-reached') {
        createToast({ content: t(TOLGEE_GLOBALS.nicheLimitMessage(NICHE_LIMIT)) })
        return
      }
      if (toggle.status === 'min-reached') {
        createToast({ content: t(nicheTranslations.minNiche) })
        return
      }
      setSelectedIds(toggle.selectedIds)
    },
    [availableSelectedIds, createToast, defaultIds, t, territoryId, territoryName]
  )

  const nicheList: INicheCard[] = useMemo(
    () =>
      sortedChildren.map(child => ({
        name: child.name,
        engagement: totalsById[child.territoryId]?.totalEngagement ?? 0,
        territoryId: child.territoryId,
        id: child.territoryId.split('.')[1] ?? '',
        isLoading: !totalsById[child.territoryId],
        isVisible: visibleIds.includes(child.territoryId),
        handleClick: handleToggleChild,
      })),
    [handleToggleChild, sortedChildren, totalsById, visibleIds]
  )

  const nichesWithPerformance = useMemo(
    () =>
      sortedChildren
        .filter(child => visibleIds.includes(child.territoryId))
        .map(child => ({
          id: child.territoryId,
          name: child.name,
          totalEngagement: totalsById[child.territoryId]?.totalEngagement ?? 0,
          totalViews: totalsById[child.territoryId]?.totalViews ?? 0,
          totalPosts: totalsById[child.territoryId]?.totalPosts ?? 0,
        })),
    [sortedChildren, totalsById, visibleIds]
  )

  const territoryIds = useMemo(() => nichesWithPerformance.map(child => child.id), [nichesWithPerformance])

  const chartUniverseData = useMemo(() => {
    const total = nichesWithPerformance.reduce(
      (sum, child) => ({
        totalEngagement: sum.totalEngagement + child.totalEngagement,
        totalPosts: sum.totalPosts + child.totalPosts,
        totalViews: sum.totalViews + child.totalViews,
      }),
      { totalEngagement: 0, totalPosts: 0, totalViews: 0 }
    )
    return `
      ${humanizeLargeNumber(total.totalEngagement, 2)} ${t(TOLGEE_GLOBALS.engagement)},
      ${humanizeLargeNumber(total.totalPosts, 2)} ${t(TOLGEE_GLOBALS.videos)},
      ${humanizeLargeNumber(total.totalViews, 2)} ${t(TOLGEE_GLOBALS.views)}
    `
  }, [nichesWithPerformance, t])

  return {
    feature,
    territoryName,
    nicheList,
    isLoading: isChildrenLoading,
    hasError,
    isEmpty,
    onRetry: () => setRetryCount(count => count + 1),
    isTotalsLoading,
    territoryIds,
    nichesWithPerformance,
    isChartReady: !isTotalsLoading && !hasError && sortedChildren.length > 0,
    chartUniverseData,
    hasReachedSelectedNicheLimit: !availableSelectedIds.length || availableSelectedIds.length >= NICHE_LIMIT,
    shouldDisplayResetButton: availableSelectedIds.length > 0,
    handleReset: () => setSelectedIds([]),
  }
}
